import type { Translate } from '../i18n/core'
import type { CouponReward } from '../services/couponApi'
import type { WhowsetLanguage } from '../types/whowset'
import { shopArtwork } from '../components/shopArtwork'

export function describeCouponReward(reward: CouponReward, locale?: string, t: Translate = key => key) {
    const type = reward.type.toLowerCase()
    const fallbackLabels: Record<string, string> = { chips: 'Chips', currency: 'Chips', joker: 'Joker', jackpotjoker: 'Joker' }
    const label = reward.presentation?.label || (fallbackLabels[type] ? t(fallbackLabels[type]) : reward.type)
    const icon = /^(chips|currency)$/.test(type) ? shopArtwork.coin : /joker/.test(type) ? shopArtwork.joker
        : type === 'leaguepoints' ? shopArtwork.league : type === 'loyaltypoints' ? shopArtwork.loyalty
            : /troph/.test(type) ? shopArtwork.trophy : shopArtwork.gift
    // A legacy/unknown reward remains identifiable without inventing translated copy.
    const details = reward.presentation?.details ?? [reward.subType, reward.extra].filter(value => value !== undefined).join(' · ')
    let formatter: Intl.NumberFormat
    try { formatter = new Intl.NumberFormat((reward.presentation?.locale || locale)?.replace(/_/g, '-')) }
    catch { formatter = new Intl.NumberFormat(locale) }
    return { label, icon, details, amount: `${type === 'paymentmulti' ? '×' : '+'}${formatter.format(reward.amount)}` }
}

/** Same inventory selectors used by the web reward metadata service. */
export function couponRewardIconSelector(reward: CouponReward): string | null {
    const type = reward.type.toLowerCase()
    const paths: Record<string, string> = {
        chips: 'chips-stacks/chipstack-4.png', currency: 'chips-stacks/chipstack-4.png',
        jackpotjoker: 'jackpot-joker/jackpot-joker.png', joker: 'jackpot-joker/jackpot-joker.png',
        loyaltypoints: 'loyalty/points.png', leaguepoints: 'league/points.png', premium: 'premium/premium.png',
        freespins: 'free-spins/fs_badge_spinamount.png',
        'clubs-level-booster-spin': 'club/jc-booster-level.png', 'clubs-challenge-booster-spin': 'club/jc-booster.png',
        symbolsspinbooster: 'symbols/js-booster-ico.png', 'symbols-booster-spin': 'symbols/js-booster-ico.png',
        premiumshopcurrency: 'premiumshop/currency.png',
    }
    let path = paths[type]
    const variant = String(reward.subType || '').replace(/^x/i, '')
    const safeVariant = /^[0-9]+(?:\.[0-9]+)?$/.test(variant) ? variant : undefined
    if (type === 'paymentmulti') path = `payment-multiplier/${String(reward.amount).replace('.', '-')}.png`
    if (['paymentbonus', 'paymentbonus50', 'payment-booster'].includes(type)) path = `payment-multiplier/${(safeVariant || '1.5').replace('.', '-')}.png`
    if (['xpspinbooster', 'xpboost-spin', 'xpboost'].includes(type)) path = `xp-booster/${safeVariant || '2'}.png`
    if (['personaljackpotspinbooster', 'personal-jackpot-booster-spin'].includes(type)) path = `personal-jackpots/booster-${safeVariant || '2'}.png`
    if (type === 'sticker-pack') {
        const colors: Record<string, string> = { 'tier-1': 'green', 'tier-2': 'yellow', 'tier-3': 'pink', 'tier-4': 'blue', 'tier-5': 'purple', galaxy: 'galaxy', cosmos: 'cosmos' }
        if (reward.subType && colors[reward.subType]) path = `collectables-stickers/sticker-pack-${colors[reward.subType]}.png`
    }
    if (['fortunaswheeltoken', 'fortunas-wheel-token', 'fortunaswheelticket'].includes(type) && reward.subType && /^r[1-5]$/.test(reward.subType)) {
        path = `fortunas-wheel/${type === 'fortunaswheelticket' ? 'ticket' : 'token'}-s1-${reward.subType}.png`
    }
    return path ? `assets/inventory-items-2/${path}` : null
}

/** Match the catalog locale to an available whowset language variant. */
export function couponRewardLanguage(locale?: string): WhowsetLanguage {
    const normalized = locale?.replace(/_/g, '-').toLowerCase()
    const language = normalized === 'pt-br' ? normalized : normalized?.split('-')[0]
    const languages: readonly WhowsetLanguage[] = ['cs', 'de', 'en', 'es', 'fr', 'it', 'nl', 'pl', 'ru', 'tr', 'hu', 'ro', 'da', 'sv', 'pt', 'pt-br']
    return languages.find(candidate => candidate === language) ?? 'global'
}
